'use client';
import { useMemo } from 'react';
import { MdClose, MdInfo } from 'react-icons/md';
import { useWallet } from '@solana/wallet-adapter-react';

import { useRecoilValue } from 'recoil';
import {
  isUserPermittedState,
  isWLPhaseState,
  userSOLBalanceState,
  userTokenBalanceState,
} from '@/lib/state';

import PendingBidRow from '@/components/PendingBidRow';
import { LAUNCHPAD_FEE } from '@/lib/constants';
import { lamportsToSol, tokenToLamports, lamportsToToken } from '@/lib/utils';

export default function BidsSummary({ bids, project, onSubmit, onRemove, onClear }) {
  const balance = useRecoilValue(userSOLBalanceState);
  const tokenBalance = useRecoilValue(userTokenBalanceState);
  const isUserPermitted = useRecoilValue(isUserPermittedState);
  const isWlPhase = useRecoilValue(isWLPhaseState);
  const { publicKey } = useWallet();
  const { bid_fee, decimals, token } = project;

  const totalNumBids = useMemo(() => {
    if (!bids) {
      return 0;
    }
    return Object.values(bids).reduce((acc, x) => acc + x, 0);
  }, [bids]);

  const subtotal = useMemo(() => {
    if (!bids) {
      return 0;
    }
    return Object.entries(bids).reduce(
      (acc, [amt, qty]) => acc + tokenToLamports(decimals, Number(amt) * Number(qty)),
      0
    );
  }, [decimals, bids]);

  const fees = useMemo(() => {
    if (!bids) {
      return 0;
    }
    return Object.entries(bids).reduce((acc, [, qty]) => acc + qty * (bid_fee || LAUNCHPAD_FEE), 0);
  }, [bids, bid_fee]);

  if (!project) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-2 font-thin">
      <p className="font-medium mb-1">{!totalNumBids && 'No '}Pending Bids</p>
      {Object.entries(bids)
        .sort(([aa, aq], [ba, bq]) => Number(ba * bq) - Number(aa * aq))
        .map(([amt, qty]) => (
          <PendingBidRow
            key={`${project.id}-${amt}-${qty}`}
            amt={amt}
            qty={qty}
            token={token}
            decimals={decimals}
            onDelete={() => onRemove(amt)}
          />
        ))}
      {totalNumBids > 0 && (
        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between px-2 font-normal">
            <p>{totalNumBids} Total Bids</p>

            <div className="flex items-center space-x-2">
              <p>
                {lamportsToToken(decimals, subtotal)} {token === 'SOL' ? '◎' : token}
              </p>
              <button className="btn btn-square btn-sm btn-ghost" onClick={onClear}>
                <MdClose />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center space-x-1">
              <p>Fee</p>
              <div className="tooltip tooltip-right" data-tip="Platfrom and Anti-bot fee">
                <MdInfo />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <p>{lamportsToSol(fees)} ◎</p>
              <div className="w-8" />
            </div>
          </div>
          <div className="h-1" />
        </div>
      )}
      {publicKey && isUserPermitted && token === 'SOL' ? (
        <button
          className={`btn ${
            totalNumBids > 0 && subtotal + fees <= balance ? 'btn-primary' : 'btn-disabled disabled'
          }`}
          onClick={() => onSubmit()}
        >
          {subtotal + fees > balance
            ? `Insufficient funds (${lamportsToSol(balance)} SOL)`
            : 'Submit Bids'}
        </button>
      ) : publicKey && isUserPermitted ? (
        <button
          className={`btn ${
            totalNumBids > 0 && subtotal <= tokenBalance && fees <= balance
              ? 'btn-primary'
              : 'btn-disabled disabled'
          }`}
          onClick={() => onSubmit()}
        >
          {subtotal <= tokenBalance && fees <= balance
            ? 'Submit Bids'
            : `Insufficient funds (${lamportsToToken(decimals, tokenBalance)} ${token})`}
        </button>
      ) : !publicKey ? (
        <button className="btn btn-disabled cursor-not-allowed">Connect Wallet To Bid</button>
      ) : isWlPhase && !isUserPermitted ? (
        <button className="btn btn-disabled cursor-not-allowed">Wallet not on WL</button>
      ) : (
        <button className="btn btn-disabled cursor-not-allowed">No Bidding Allowed</button>
      )}
    </div>
  );
}
